import React, { useState } from 'react';
import { Building2Icon, PlusIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon } from 'lucide-react';
import { Vendor, VendorAccount } from '../types/vendor';
interface VendorAccountsSectionProps {
  vendor: Vendor;
  centralizedBankAccounts: any[];
  centralizedContacts: any[];
  centralizedAddresses: any[];
  onUpdate: (vendor: Vendor) => void;
  isEditing: boolean;
  locale: string;
}
export function VendorAccountsSection({
  vendor,
  centralizedBankAccounts,
  centralizedContacts,
  centralizedAddresses,
  onUpdate,
  isEditing,
  locale
}: VendorAccountsSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<VendorAccount | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  // Form state
  const [formData, setFormData] = useState({
    vendorCode: '',
    vendorCodeCustom: false,
    accountNumber: '',
    accountName: '',
    orgUnitId: '',
    paymentTermCode: '',
    paymentTermDescription: '',
    calculationBasis: 'InvoiceDate',
    dueDays: 30,
    daysAfterEndOfMonth: '',
    discountPercent: '',
    discountDays: '',
    preferredBankAccountId: '',
    isActive: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Mock OrgUnits (same as VendorDetails)
  const availableOrgUnits = [{
    id: 'org-001',
    code: 'GLOBAL',
    name: 'Global Technologies Inc.',
    path: '/GLOBAL'
  }, {
    id: 'org-002',
    code: 'NY-HQ',
    name: 'New York Headquarters',
    path: '/GLOBAL/NY-HQ'
  }, {
    id: 'org-005',
    code: 'NY-FIN',
    name: 'Finance Department',
    path: '/GLOBAL/NY-HQ/NY-FIN'
  }, {
    id: 'org-003',
    code: 'SF-OFFICE',
    name: 'San Francisco Office',
    path: '/GLOBAL/SF-OFFICE'
  }, {
    id: 'org-004',
    code: 'EURO-OPS',
    name: 'European Operations Ltd',
    path: '/EURO-OPS'
  }, {
    id: 'org-006',
    code: 'APAC-PTY',
    name: 'Asia Pacific Technologies Pty Ltd',
    path: '/APAC-PTY'
  }, {
    id: 'org-007',
    code: 'SYD-OFFICE',
    name: 'Sydney Office',
    path: '/APAC-PTY/SYD-OFFICE'
  }];
  const toggleExpand = (accountId: string) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };
  const handleAddClick = () => {
    setEditingAccount(null);
    // Auto-generate vendor code: vendor.code + "-" + next sequence number
    const existingAccounts = vendor.accounts || [];
    const nextSeq = existingAccounts.length + 1;
    const baseCode = (vendor as any).code || (vendor as any).vendorCode || '';
    const autoCode = baseCode ? `${baseCode}-${String(nextSeq).padStart(2, '0')}` : '';
    setFormData({
      vendorCode: autoCode,
      vendorCodeCustom: false,
      accountNumber: '',
      accountName: '',
      orgUnitId: '',
      paymentTermCode: '',
      paymentTermDescription: '',
      calculationBasis: 'InvoiceDate',
      dueDays: 30,
      daysAfterEndOfMonth: '',
      discountPercent: '',
      discountDays: '',
      preferredBankAccountId: '',
      isActive: true
    });
    setErrors({});
    setShowForm(true);
  };
  const handleEditClick = (account: VendorAccount) => {
    setEditingAccount(account);
    // Map existing payment term if any
    const pt = account.paymentTerm;
    let calcBasis = 'InvoiceDate';
    if (pt) {
      calcBasis = pt.calculationBasis || (pt.isEndOfMonth ? 'EndOfMonth' : 'InvoiceDate');
    }
    setFormData({
      vendorCode: account.vendorCode || '',
      vendorCodeCustom: !!account.vendorCode,
      accountNumber: account.accountNumber || '',
      accountName: account.accountName || '',
      orgUnitId: account.orgUnitId || '',
      paymentTermCode: pt?.code || '',
      paymentTermDescription: pt?.description || '',
      calculationBasis: calcBasis,
      dueDays: pt?.dueDays ?? 30,
      daysAfterEndOfMonth: pt?.daysAfterEndOfMonth ?? '',
      discountPercent: pt?.discountPercent ?? '',
      discountDays: pt?.discountDays ?? '',
      preferredBankAccountId: account.preferredBankAccountId || '',
      isActive: account.isActive ?? true
    });
    setErrors({});
    setShowForm(true);
  };
  const handleDeleteClick = (accountId: string) => {
    if (window.confirm('Are you sure you want to delete this trading account?')) {
      const updatedAccounts = (vendor.accounts || []).filter(a => a.id !== accountId);
      onUpdate({
        ...vendor,
        accounts: updatedAccounts
      });
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.vendorCode.trim()) {
      newErrors.vendorCode = 'Vendor code is required';
    } else {
      // Check uniqueness of vendor code
      const isDuplicateCode = (vendor.accounts || []).some(a => a.vendorCode && a.vendorCode.toLowerCase() === formData.vendorCode.trim().toLowerCase() && a.id !== editingAccount?.id);
      if (isDuplicateCode) {
        newErrors.vendorCode = 'Vendor code must be unique for this vendor';
      }
    }
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else {
      // Check uniqueness
      const isDuplicate = (vendor.accounts || []).some(a => a.accountNumber.toLowerCase() === formData.accountNumber.trim().toLowerCase() && a.id !== editingAccount?.id);
      if (isDuplicate) {
        newErrors.accountNumber = 'Account number must be unique for this vendor';
      }
    }
    // Validate payment term if partially filled
    const hasPtCode = !!formData.paymentTermCode.trim();
    const hasPtDesc = !!formData.paymentTermDescription.trim();
    if (hasPtCode || hasPtDesc) {
      if (!hasPtCode) newErrors.paymentTermCode = 'Code is required if defining a payment term';
      if (!hasPtDesc) newErrors.paymentTermDescription = 'Description is required if defining a payment term';
      if (formData.calculationBasis === 'InvoiceDate') {
        if (formData.dueDays === '' || formData.dueDays === null || Number(formData.dueDays) < 0) {
          newErrors.dueDays = 'Due days must be 0 or more';
        }
      }
      if (formData.calculationBasis === 'EndOfMonth' && formData.daysAfterEndOfMonth !== '' && Number(formData.daysAfterEndOfMonth) < 0) {
        newErrors.daysAfterEndOfMonth = 'Days must be 0 or more';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (!validateForm()) return;
    let paymentTerm = null;
    if (formData.paymentTermCode.trim()) {
      paymentTerm = {
        id: editingAccount?.paymentTerm?.id || `pt-${Date.now()}`,
        code: formData.paymentTermCode.trim(),
        description: formData.paymentTermDescription.trim(),
        calculationBasis: formData.calculationBasis as 'InvoiceDate' | 'EndOfMonth' | 'Immediate',
        dueDays: formData.calculationBasis === 'InvoiceDate' ? Number(formData.dueDays) : null,
        isEndOfMonth: formData.calculationBasis === 'EndOfMonth',
        daysAfterEndOfMonth: formData.calculationBasis === 'EndOfMonth' && formData.daysAfterEndOfMonth !== '' ? Number(formData.daysAfterEndOfMonth) : null,
        discountPercent: formData.discountPercent ? Number(formData.discountPercent) : null,
        discountDays: formData.discountDays ? Number(formData.discountDays) : null,
        createdAt: editingAccount?.paymentTerm?.createdAt || new Date().toISOString(),
        createdById: editingAccount?.paymentTerm?.createdById || 'system'
      };
    }
    const orgUnit = formData.orgUnitId ? availableOrgUnits.find(o => o.id === formData.orgUnitId) : null;
    const newAccount: VendorAccount = {
      id: editingAccount?.id || `va-${Date.now()}`,
      vendorId: vendor.id,
      vendorCode: formData.vendorCode.trim() || null,
      accountNumber: formData.accountNumber.trim(),
      accountName: formData.accountName.trim() || null,
      orgUnitId: formData.orgUnitId || null,
      orgUnit: orgUnit ? {
        id: orgUnit.id,
        code: orgUnit.code,
        name: orgUnit.name,
        path: orgUnit.path
      } : null,
      paymentTermId: paymentTerm ? paymentTerm.id : null,
      paymentTerm: paymentTerm,
      preferredBankAccountId: formData.preferredBankAccountId || null,
      contacts: editingAccount?.contacts || [],
      addresses: editingAccount?.addresses || [],
      isActive: formData.isActive,
      createdAt: editingAccount?.createdAt || new Date().toISOString(),
      createdById: editingAccount?.createdById || 'system'
    };
    let updatedAccounts = vendor.accounts || [];
    if (editingAccount) {
      updatedAccounts = updatedAccounts.map(a => a.id === editingAccount.id ? newAccount : a);
    } else {
      updatedAccounts = [...updatedAccounts, newAccount];
    }
    onUpdate({
      ...vendor,
      accounts: updatedAccounts
    });
    setShowForm(false);
    setEditingAccount(null);
  };
  const accounts = vendor.accounts || [];
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21538">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center" data-id="element-21539">
        <div data-id="element-21540">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-21541">
            <Building2Icon className="h-5 w-5 mr-2 text-gray-400" data-id="element-21542" />
            Trading Accounts
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21543">
            Commercial trading relationships for this vendor. Optional — simple
            vendors may not need accounts.
          </p>
        </div>
        {(isEditing || accounts.length > 0) && !showForm && <button type="button" onClick={handleAddClick} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21544">
            <PlusIcon className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" data-id="element-21545" />
            Add Account
          </button>}
      </div>

      {showForm ? <div className="p-5 bg-gray-50 border-b border-gray-200" data-id="element-21546">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-21547">
            {editingAccount ? 'Edit Trading Account' : 'Add Trading Account'}
          </h4>

          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-21548">
            <div className="sm:col-span-3" data-id="element-21549">
              <label htmlFor="vendorCode" className="block text-sm font-medium text-gray-700" data-id="element-21550">
                Vendor Code *
              </label>
              <div className="mt-1 flex rounded-md shadow-sm" data-id="element-21551">
                <input type="text" name="vendorCode" id="vendorCode" value={formData.vendorCode} onChange={e => {
              handleInputChange(e);
              setFormData(prev => ({
                ...prev,
                vendorCodeCustom: true
              }));
            }} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md font-mono ${errors.vendorCode ? 'border-red-300' : ''}`} placeholder="e.g. OD-001-01" data-id="element-21552" />
              </div>
              {errors.vendorCode ? <p className="mt-1 text-sm text-red-600" data-id="element-21553">{errors.vendorCode}</p> : <p className="mt-1 text-xs text-gray-500" data-id="element-21554">
                  Auto-generated from vendor code. Edit to customise.
                </p>}
            </div>

            <div className="sm:col-span-3" data-id="element-21555">
              <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700" data-id="element-21556">
                Account Number *
              </label>
              <input type="text" name="accountNumber" id="accountNumber" value={formData.accountNumber} onChange={handleInputChange} className={`mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.accountNumber ? 'border-red-300' : ''}`} data-id="element-21557" />
              {errors.accountNumber && <p className="mt-1 text-sm text-red-600" data-id="element-21558">
                  {errors.accountNumber}
                </p>}
            </div>

            <div className="sm:col-span-3" data-id="element-21559">
              <label htmlFor="accountName" className="block text-sm font-medium text-gray-700" data-id="element-21560">
                Account Name
              </label>
              <input type="text" name="accountName" id="accountName" value={formData.accountName} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-21561" />
            </div>

            <div className="sm:col-span-3" data-id="element-21562">
              <label htmlFor="orgUnitId" className="block text-sm font-medium text-gray-700" data-id="element-21563">
                Organization Unit
              </label>
              <select id="orgUnitId" name="orgUnitId" value={formData.orgUnitId} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-21564">
                <option value="" data-id="element-21565">Global (All Entities)</option>
                {availableOrgUnits.map(ou => <option key={ou.id} value={ou.id} data-id="element-21566">
                    {ou.path} - {ou.name}
                  </option>)}
              </select>
            </div>

            <div className="sm:col-span-3" data-id="element-21567">
              <label htmlFor="preferredBankAccountId" className="block text-sm font-medium text-gray-700" data-id="element-21568">
                Preferred Bank Account
              </label>
              <select id="preferredBankAccountId" name="preferredBankAccountId" value={formData.preferredBankAccountId} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-21569">
                <option value="" data-id="element-21570">Use vendor primary account</option>
                {vendor.bankAccountIds?.map(id => {
              const bank = centralizedBankAccounts.find(b => b.id === id);
              if (!bank) return null;
              return <option key={id} value={id} data-id="element-21571">
                      {bank.bankName} - {bank.accountNumber.slice(-4)}
                    </option>;
            })}
              </select>
            </div>

            <div className="sm:col-span-6" data-id="element-21572">
              <div className="flex items-center h-5" data-id="element-21573">
                <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-21574" />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900" data-id="element-21575">
                  Account is active
                </label>
              </div>
            </div>

            {/* Payment Term Override */}
            <div className="sm:col-span-6 border-t border-gray-200 pt-5" data-id="element-21576">
              <h5 className="text-sm font-medium text-gray-900 mb-4" data-id="element-21577">
                Payment Term Override (Optional)
              </h5>
              <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-21578">
                <div className="sm:col-span-2" data-id="element-21579">
                  <label htmlFor="paymentTermCode" className="block text-sm font-medium text-gray-700" data-id="element-21580">
                    Term Code
                  </label>
                  <input type="text" name="paymentTermCode" id="paymentTermCode" value={formData.paymentTermCode} onChange={handleInputChange} placeholder="e.g. NET30" className={`mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.paymentTermCode ? 'border-red-300' : ''}`} data-id="element-21581" />
                  {errors.paymentTermCode && <p className="mt-1 text-xs text-red-600" data-id="element-21582">
                      {errors.paymentTermCode}
                    </p>}
                </div>
                <div className="sm:col-span-4" data-id="element-21583">
                  <label htmlFor="paymentTermDescription" className="block text-sm font-medium text-gray-700" data-id="element-21584">
                    Description
                  </label>
                  <input type="text" name="paymentTermDescription" id="paymentTermDescription" value={formData.paymentTermDescription} onChange={handleInputChange} placeholder="e.g. Net 30 Days" className={`mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.paymentTermDescription ? 'border-red-300' : ''}`} data-id="element-21585" />
                  {errors.paymentTermDescription && <p className="mt-1 text-xs text-red-600" data-id="element-21586">
                      {errors.paymentTermDescription}
                    </p>}
                </div>

                <div className="sm:col-span-2" data-id="element-21587">
                  <label htmlFor="calculationBasis" className="block text-sm font-medium text-gray-700" data-id="element-21588">
                    Calculation Basis
                  </label>
                  <select id="calculationBasis" name="calculationBasis" value={formData.calculationBasis} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-21589">
                    <option value="InvoiceDate" data-id="element-21590">Invoice Date</option>
                    <option value="EndOfMonth" data-id="element-21591">End of Month</option>
                    <option value="Immediate" data-id="element-21592">Immediate</option>
                  </select>
                </div>

                {formData.calculationBasis === 'InvoiceDate' && <div className="sm:col-span-2" data-id="element-21593">
                    <label htmlFor="dueDays" className="block text-sm font-medium text-gray-700" data-id="element-21594">
                      Due Days
                    </label>
                    <input type="number" name="dueDays" id="dueDays" min="0" value={formData.dueDays} onChange={handleInputChange} className={`mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.dueDays ? 'border-red-300' : ''}`} data-id="element-21595" />
                    {errors.dueDays && <p className="mt-1 text-xs text-red-600" data-id="element-21596">
                        {errors.dueDays}
                      </p>}
                  </div>}

                {formData.calculationBasis === 'EndOfMonth' && <div className="sm:col-span-2" data-id="element-21597">
                    <label htmlFor="daysAfterEndOfMonth" className="block text-sm font-medium text-gray-700" data-id="element-21598">
                      Days After EOM
                    </label>
                    <input type="number" name="daysAfterEndOfMonth" id="daysAfterEndOfMonth" min="0" value={formData.daysAfterEndOfMonth} onChange={handleInputChange} placeholder="e.g. 30" className={`mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.daysAfterEndOfMonth ? 'border-red-300' : ''}`} data-id="element-21599" />
                    {errors.daysAfterEndOfMonth && <p className="mt-1 text-xs text-red-600" data-id="element-21600">
                        {errors.daysAfterEndOfMonth}
                      </p>}
                  </div>}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3" data-id="element-21601">
            <button type="button" onClick={() => {
          setShowForm(false);
          setEditingAccount(null);
        }} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21602">
              Cancel
            </button>
            <button type="button" onClick={handleSave} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21603">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-21604" />
              Save Account
            </button>
          </div>
        </div> : accounts.length > 0 ? <div className="px-4 py-5 sm:p-6" data-id="element-21605">
          <div className="space-y-4" data-id="element-21606">
            {accounts.map(account => {
          const isExpanded = expandedAccounts[account.id];
          const bankName = account.preferredBankAccountId ? centralizedBankAccounts.find(b => b.id === account.preferredBankAccountId)?.bankName : null;
          return <div key={account.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-21607">
                  <div className="px-4 py-4 sm:px-6 flex items-center justify-between bg-gray-50" data-id="element-21608">
                    <div className="flex items-center space-x-4" data-id="element-21609">
                      <button onClick={() => toggleExpand(account.id)} className="text-gray-400 hover:text-gray-600 focus:outline-none" data-id="element-21610">
                        {isExpanded ? <ChevronUpIcon className="h-5 w-5" data-id="element-21611" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-21612" />}
                      </button>
                      <div data-id="element-21613">
                        <div className="flex items-center space-x-2" data-id="element-21614">
                          {account.vendorCode && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-indigo-100 text-indigo-800" data-id="element-21615">
                              {account.vendorCode}
                            </span>}
                          <span className="font-mono font-bold text-gray-900" data-id="element-21616">
                            {account.accountNumber}
                          </span>
                          {account.isActive ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-21617">
                              Active
                            </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-21618">
                              Inactive
                            </span>}
                        </div>
                        {account.accountName && <p className="text-sm text-gray-500 mt-0.5" data-id="element-21619">
                            {account.accountName}
                          </p>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-21620">
                      <button onClick={() => handleEditClick(account)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors" title="Edit Account" data-id="element-21621">
                        <EditIcon className="h-4 w-4" data-id="element-21622" />
                      </button>
                      <button onClick={() => handleDeleteClick(account.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors" title="Delete Account" data-id="element-21623">
                        <TrashIcon className="h-4 w-4" data-id="element-21624" />
                      </button>
                    </div>
                  </div>

                  {isExpanded && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-21625">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-21626">
                        <div data-id="element-21627">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-21628">
                            Vendor Code
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 font-mono" data-id="element-21629">
                            {account.vendorCode || <span className="text-gray-400 italic font-sans" data-id="element-21630">
                                Not set
                              </span>}
                          </dd>
                        </div>
                        <div data-id="element-21631">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-21632">
                            Organization Unit
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-21633">
                            {account.orgUnit ? account.orgUnit.name : 'Global (All Entities)'}
                          </dd>
                        </div>
                        <div data-id="element-21634">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-21635">
                            Payment Term Override
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-21636">
                            {account.paymentTerm ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-21637">
                                {account.paymentTerm.code} -{' '}
                                {account.paymentTerm.description}
                              </span> : <span className="text-gray-500 italic" data-id="element-21638">
                                Inherits from vendor
                              </span>}
                          </dd>
                        </div>
                        <div data-id="element-21639">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-21640">
                            Preferred Bank Account
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-21641">
                            {bankName ? <span className="flex items-center" data-id="element-21642">
                                <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1.5" data-id="element-21643" />
                                {bankName}
                              </span> : <span className="text-gray-500 italic" data-id="element-21644">
                                Uses vendor primary account
                              </span>}
                          </dd>
                        </div>
                        <div data-id="element-21645">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-21646">
                            Account Contacts & Addresses
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-21647">
                            {account.contacts?.length || 0} contacts,{' '}
                            {account.addresses?.length || 0} addresses
                          </dd>
                        </div>
                      </dl>
                    </div>}
                </div>;
        })}
          </div>
        </div> : <div className="text-center py-10 px-4" data-id="element-21648">
          <Building2Icon className="mx-auto h-12 w-12 text-gray-300" data-id="element-21649" />
          <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-21650">
            No trading accounts
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-21651">
            This vendor operates as a simple vendor with a single commercial
            relationship.
          </p>
          {isEditing && <div className="mt-6" data-id="element-21652">
              <button type="button" onClick={handleAddClick} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21653">
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" data-id="element-21654" />
                Add Account
              </button>
            </div>}
        </div>}
    </div>;
}